import { Config } from '@/config/environment';
import { getAuthConfig } from './providers';

interface TokenResponse {
  access_token: string;
  refresh_token?: string;
  expires_in: number;
  token_type: string;
  scope?: string;
  id_token?: string;
}

interface UserInfo {
  sub: string;
  name?: string;
  email?: string;
  preferred_username?: string;
  roles?: string[];
  [key: string]: any;
}

const REFRESH_BUFFER_MS = 5 * 60 * 1000; // 5 minutes before expiry

export class SFAuth {
  private static instance: SFAuth;
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private idToken: string | null = null;
  private tokenExpiry: number | null = null;
  private userInfo: UserInfo | null = null;
  private refreshPromise: Promise<void> | null = null;

  private constructor() {
    this.loadTokensFromStorage();
  }

  static getInstance(): SFAuth {
    if (!SFAuth.instance) {
      SFAuth.instance = new SFAuth();
    }
    return SFAuth.instance;
  }

  // PKCE helper methods
  private async generateCodeVerifier(): Promise<string> {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return this.base64URLEncode(array);
  }

  private async generateCodeChallenge(verifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const hash = await crypto.subtle.digest('SHA-256', data);
    return this.base64URLEncode(new Uint8Array(hash));
  }

  private base64URLEncode(buffer: Uint8Array): string {
    const base64 = btoa(String.fromCharCode(...buffer));
    return base64
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }

  // OAuth flow methods
  async startAuthFlow(): Promise<void> {
    const config = getAuthConfig();
    const codeVerifier = await this.generateCodeVerifier();
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);
    const state = crypto.randomUUID();

    // Store PKCE values
    sessionStorage.setItem('pkce_code_verifier', codeVerifier);
    sessionStorage.setItem('oauth_state', state);

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: Config.auth.clientId,
      redirect_uri: config.redirectUri,
      scope: config.scopes.join(' '),
      state,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    });

    if (Config.auth.tenantId) {
      params.append('tenantId', Config.auth.tenantId);
    }

    window.location.href = `${config.authUrl}?${params.toString()}`;
  }

  async handleOAuthCallback(code: string, state: string): Promise<void> {
    const storedState = sessionStorage.getItem('oauth_state');
    const codeVerifier = sessionStorage.getItem('pkce_code_verifier');

    if (state !== storedState) {
      throw new Error('Invalid state parameter');
    }

    if (!codeVerifier) {
      throw new Error('No code verifier found');
    }

    const tokenResponse = await this.exchangeCodeForToken(code, codeVerifier);
    
    this.processTokenResponse(tokenResponse);
    await this.fetchUserInfo();

    // Clean up session storage
    sessionStorage.removeItem('oauth_state');
    sessionStorage.removeItem('pkce_code_verifier');
  }

  private async exchangeCodeForToken(code: string, codeVerifier: string): Promise<TokenResponse> {
    const config = getAuthConfig();
    
    const params = new URLSearchParams({
      grant_type: 'authorization_code',
      code,
      redirect_uri: config.redirectUri,
      client_id: Config.auth.clientId,
      code_verifier: codeVerifier,
    });

    const response = await fetch(config.tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params.toString(),
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Token exchange failed: ${error}`);
    }

    return response.json();
  }

  private processTokenResponse(tokenResponse: TokenResponse): void {
    this.accessToken = tokenResponse.access_token;
    if (tokenResponse.id_token) {
      this.idToken = tokenResponse.id_token;
    }
    if (tokenResponse.refresh_token) {
      this.refreshToken = tokenResponse.refresh_token;
    }
    this.tokenExpiry = Date.now() + tokenResponse.expires_in * 1000;

    // Store tokens
    localStorage.setItem('access_token', this.accessToken);
    if (this.idToken) {
      localStorage.setItem('id_token', this.idToken);
    }
    if (this.refreshToken) {
      localStorage.setItem('refresh_token', this.refreshToken);
    }
    localStorage.setItem('token_expiry', this.tokenExpiry.toString());
  }

  private async fetchUserInfo(): Promise<void> {
    const { userInfoUrl } = getAuthConfig();
    
    if (!this.accessToken) {
      throw new Error('No access token available');
    }

    const response = await fetch(userInfoUrl, {
      headers: {
        Authorization: `Bearer ${this.accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch user info');
    }

    this.userInfo = await response.json();
    localStorage.setItem('user_info', JSON.stringify(this.userInfo));
  }

  // Token management
  private loadTokensFromStorage(): void {
    this.accessToken = localStorage.getItem('access_token');
    this.refreshToken = localStorage.getItem('refresh_token');
    this.idToken = localStorage.getItem('id_token');
    const expiryStr = localStorage.getItem('token_expiry');
    this.tokenExpiry = expiryStr ? parseInt(expiryStr) : null;
    
    const userInfoStr = localStorage.getItem('user_info');
    if (userInfoStr) {
      try {
        this.userInfo = JSON.parse(userInfoStr);
      } catch {
        this.userInfo = null;
      }
    }
  }

  async getAccessToken(forceRefresh = false): Promise<string | null> {
    // Try to recover from cleared in-memory state
    if (!this.accessToken || !this.refreshToken) {
      this.loadTokensFromStorage();
    }

    // No access token AND no refresh token — nothing we can do
    if (!this.accessToken && !this.refreshToken) {
      return null;
    }

    // Refresh when: forced, unknown expiry (tokenExpiry null), or within buffer of expiry
    const needsRefresh = forceRefresh || !this.tokenExpiry || Date.now() + REFRESH_BUFFER_MS >= this.tokenExpiry;

    if (needsRefresh && this.refreshToken) {
      await this.refreshAccessToken();
    }

    return this.accessToken;
  }

  private async refreshAccessToken(): Promise<void> {
    // Prevent multiple simultaneous refresh attempts
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.performTokenRefresh();
    
    try {
      await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async performTokenRefresh(): Promise<void> {
    if (!this.refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await fetch(`${Config.auth.url}/api/jwt/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        refreshToken: this.refreshToken,
        token: this.accessToken,
      }),
    });

    if (!response.ok) {
      console.warn(`Token refresh failed with status ${response.status}`);

      // Always clear access token and expiry — they're invalid
      this.accessToken = null;
      this.tokenExpiry = null;
      localStorage.removeItem('access_token');
      localStorage.removeItem('token_expiry');

      // Only clear refresh token on 4xx (auth server definitively rejected it)
      // Preserve on 5xx so we can retry after transient server errors
      if (response.status < 500) {
        this.refreshToken = null;
        localStorage.removeItem('refresh_token');
      }

      throw new Error('Token refresh failed');
    }

    const data = await response.json();
    const newToken: string = data.token;
    this.accessToken = newToken;
    if (data.refreshToken) {
      this.refreshToken = data.refreshToken;
    }

    // Parse expiry from the JWT payload since /api/jwt/refresh doesn't return expires_in
    try {
      const payload = JSON.parse(atob(newToken.split('.')[1]));
      this.tokenExpiry = payload.exp * 1000;
    } catch {
      // Fallback: assume 1 hour
      this.tokenExpiry = Date.now() + 3600 * 1000;
    }

    localStorage.setItem('access_token', newToken);
    if (this.refreshToken) {
      localStorage.setItem('refresh_token', this.refreshToken);
    }
    localStorage.setItem('token_expiry', this.tokenExpiry.toString());
  }

  /**
   * Checks the session by attempting to get a valid access token.
   * Returns the access token string if successful, or empty string if session is invalid.
   * Used by SessionMonitor for periodic polling.
   */
  async checkSession(): Promise<string> {
    try {
      const token = await this.getAccessToken();
      return token || '';
    } catch {
      return '';
    }
  }

  /**
   * Returns true if any tokens exist (in memory or localStorage).
   * Used to distinguish "never logged in" from "session expired."
   */
  hasSession(): boolean {
    return !!(
      this.accessToken ||
      this.refreshToken ||
      localStorage.getItem('access_token') ||
      localStorage.getItem('refresh_token')
    );
  }

  // Session management
  isAuthenticated(): boolean {
    return !!this.accessToken && !!this.tokenExpiry && this.tokenExpiry > Date.now();
  }

  getUserInfo(): UserInfo | null {
    return this.userInfo;
  }

  /**
   * Returns the role claims for the current session.
   * FusionAuth emits the application registration's roles in the JWT `roles`
   * claim; we prefer that source and fall back to the userinfo `roles` array.
   * Always returns a (possibly empty) array — never null.
   */
  getRoles(): string[] {
    const fromToken = this.extractRolesFromToken();
    if (fromToken.length > 0) {
      return fromToken;
    }

    const fromUserInfo = this.userInfo?.roles;
    return Array.isArray(fromUserInfo) ? fromUserInfo : [];
  }

  /**
   * True when the current session carries the given role claim.
   */
  hasRole(role: string): boolean {
    return this.getRoles().includes(role);
  }

  /**
   * Decodes the `roles` claim from the access token JWT payload.
   * Returns an empty array when there is no token or it cannot be parsed.
   */
  private extractRolesFromToken(): string[] {
    if (!this.accessToken) {
      return [];
    }

    try {
      const payloadSegment = this.accessToken.split('.')[1];
      if (!payloadSegment) {
        return [];
      }
      const payload = JSON.parse(atob(payloadSegment));
      return Array.isArray(payload.roles) ? payload.roles : [];
    } catch {
      return [];
    }
  }

  /**
   * Clears all authentication tokens without redirecting.
   * Use this when showing a session expired dialog instead of hard redirect.
   */
  clearSession(): void {
    this.clearTokens();
  }

  async logout(): Promise<void> {
    const { logoutUrl } = getAuthConfig();

    if (this.idToken || this.accessToken) {
      const params = new URLSearchParams({
        id_token_hint: this.idToken || this.accessToken || '',
        post_logout_redirect_uri: window.location.origin,
      });

      if (Config.auth.clientId) {
        params.append('client_id', Config.auth.clientId);
      }

      this.clearSession();
      window.location.href = `${logoutUrl}?${params.toString()}`;
    } else {
      this.clearSession();
      window.location.href = '/';
    }
  }

  private clearTokens(): void {
    this.accessToken = null;
    this.refreshToken = null;
    this.idToken = null;
    this.tokenExpiry = null;
    this.userInfo = null;

    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('id_token');
    localStorage.removeItem('token_expiry');
    localStorage.removeItem('user_info');
  }

  // API request headers
  getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};
    
    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }
    
    const appInstallId = localStorage.getItem('App-Install-Id');
    if (!appInstallId) {
      const newInstallId = crypto.randomUUID();
      localStorage.setItem('App-Install-Id', newInstallId);
      headers['App-Install-Id'] = newInstallId;
    } else {
      headers['App-Install-Id'] = appInstallId;
    }
    
    
    headers['App-Version'] = Config.releaseVersion;
    
    return headers;
  }
}

export const sfAuth = SFAuth.getInstance();